'use client';

import Link from 'next/link';
import { Calendar, Users, MapPin, Clock, ArrowRight } from 'lucide-react';
import { useTranslations } from '@/i18n/I18nProvider';

const PERSONAL_TIMETABLE_URL =
    process.env.NEXT_PUBLIC_PERSONAL_TIMETABLE_URL || 'https://hfu.digital/me/timetable';

export default function HomePage() {
    const { t } = useTranslations('home');

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f]">
            {/* Hero Section */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center mb-16">
                    <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6 font-heading">
                        {t('title')}
                        <span className="text-purple dark:text-purple-bright"> {t('titleHighlight')}</span>
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-white/60 max-w-2xl mx-auto mb-8">
                        {t('subtitle')}
                    </p>
                    <div className="flex justify-center gap-4">
                        <a
                            href={PERSONAL_TIMETABLE_URL}
                            className="bg-purple text-white px-6 py-3 rounded-lg hover:bg-purple-hover transition-colors flex items-center gap-2 shadow-sm"
                        >
                            Sign in on hfu.digital <ArrowRight className="w-5 h-5" />
                        </a>
                        <Link
                            href="/programs"
                            className="border border-gray-300 dark:border-white/10 text-gray-700 dark:text-white/80 px-6 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            {t('browsePrograms')}
                        </Link>
                    </div>
                </div>

                {/* Features */}
                <div className="grid md:grid-cols-3 gap-8 mt-16">
                    <FeatureCard
                        icon={<Clock className="w-8 h-8" />}
                        title={t('features.personalizedSchedule.title')}
                        description={t('features.personalizedSchedule.description')}
                    />
                    <FeatureCard
                        icon={<Calendar className="w-8 h-8" />}
                        title={t('features.calendarSync.title')}
                        description={t('features.calendarSync.description')}
                    />
                    <FeatureCard
                        icon={<Users className="w-8 h-8" />}
                        title={t('features.splitLectures.title')}
                        description={t('features.splitLectures.description')}
                    />
                </div>

                {/* Quick Links */}
                <div className="mt-20">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white/90 mb-8 text-center font-heading">
                        {t('quickAccess')}
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <QuickLink
                            href="/rooms"
                            icon={<MapPin className="w-6 h-6" />}
                            title={t('roomLookup.title')}
                            description={t('roomLookup.description')}
                        />
                        <QuickLink
                            href="/teachers"
                            icon={<Users className="w-6 h-6" />}
                            title={t('teacherLookup.title')}
                            description={t('teacherLookup.description')}
                        />
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-gray-200 dark:border-white/5 mt-20 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-center text-gray-500 dark:text-white/40">{t('footer')}</p>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({
    icon,
    title,
    description,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
}) {
    return (
        <div className="bg-white dark:bg-white/[0.02] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-white/5 hover:border-purple/20 dark:hover:border-purple-bright/20 transition-colors">
            <div className="text-purple dark:text-purple-bright mb-4">{icon}</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white/90 mb-2 font-heading">
                {title}
            </h3>
            <p className="text-gray-600 dark:text-white/60">{description}</p>
        </div>
    );
}

function QuickLink({
    href,
    icon,
    title,
    description,
}: {
    href: string;
    icon: React.ReactNode;
    title: string;
    description: string;
}) {
    return (
        <Link
            href={href}
            className="bg-white dark:bg-white/[0.02] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-white/5 hover:border-purple/20 dark:hover:border-purple-bright/20 transition-all flex items-center gap-4"
        >
            <div className="text-purple dark:text-purple-bright p-3 bg-purple/10 dark:bg-purple-bright/15 rounded-lg">
                {icon}
            </div>
            <div>
                <h3 className="font-semibold text-gray-900 dark:text-white/90">{title}</h3>
                <p className="text-sm text-gray-600 dark:text-white/60">{description}</p>
            </div>
        </Link>
    );
}
